import { useMemo, type ReactNode } from 'react';
import type { PanelAccessModel } from '../../foundation/access';
import type { ParteBridgeHandoff } from '../../foundation/parte-bridge/gate';
import type { PanelGeneratedHttpPort } from '../../foundation/runtime-port';
import { randomUuid } from '../../runtime/browser-crypto';
import type { RestaurantFacts } from '../../runtime/restaurant-setting';
import type { PanelRuntimeSession } from '../../runtime/session-model';
import { StaffNoticeMount, type StaffNoticeScreen } from '../../screens/staff-notice/staff-notice-mount';
import { StaffScreenNoticesProvider } from '../../foundation/staff-notice/screen-notices';
import type { ScreenRuntimeProps } from '../screen-runtime';

/**
 * What a screen's binding receives. Each screen binds its owner ports to the one client inside its own chunk
 * (`app/screen-runtime.tsx` loads the binding with the screen), so the Panel's entry carries no screen's ports.
 */
export type RestaurantScreenContext = ScreenRuntimeProps & {
  readonly session: PanelRuntimeSession & { readonly tenant: Extract<PanelRuntimeSession['tenant'], { kind: 'restaurant' }> };
  readonly access: PanelAccessModel;
  readonly facts: RestaurantFacts;
  /** Non-null for every screen that declares it needs the owning franchise. */
  readonly tenantId: string;
  /** El Parte E5: the revalidated prefill link, when the screen was opened from a card. */
  readonly bridge: ParteBridgeHandoff | null;
};
export type TenantScreenContext = ScreenRuntimeProps;

export interface ScreenBindingProps<Context> {
  readonly client: PanelGeneratedHttpPort;
  readonly context: Context;
}

/**
 * A screen's binding as a component: its owner ports are bound once per client, so a re-render (the session
 * rehydrating on focus, a connection change) hands the screen the same ports and effects keyed on a port do not restart.
 */
export function bindingComponent<Context>(bind: (client: PanelGeneratedHttpPort) => (context: Context) => ReactNode) {
  return function ScreenBinding({ client, context }: ScreenBindingProps<Context>) {
    const render = useMemo(() => bind(client), [client]);
    return <>{render(context)}</>;
  };
}

export const uuid = () => randomUuid();
export const operationKey = (_operation?: unknown) => randomUuid();
export const noOp = () => undefined;

/**
 * One value per key, rebuilt only when a key part changes. A screen binds its owner ports once per
 * client; ports that also depend on the restaurant are held here, so a re-render (the session
 * rehydrating on focus, a connection change) hands the screen the same port and effects keyed on
 * a port do not restart.
 */
export function keyedOnce<Key extends readonly unknown[], Value>(create: (...key: Key) => Value): (...key: Key) => Value {
  let held: Readonly<{ key: Key; value: Value }> | null = null;
  return (...key) => {
    if (held === null || held.key.length !== key.length || held.key.some((part, index) => !Object.is(part, key[index]))) {
      held = { key, value: create(...key) };
    }
    return held.value;
  };
}

/** TPV, Cocina and Servicio show the team's notice in their quiet corner, beside the screen and never inside it. */
export const withStaffNotice = (screen: StaffNoticeScreen, view: ReactNode) =>
  <StaffScreenNoticesProvider>
    {view}
    <StaffNoticeMount screen={screen} />
  </StaffScreenNoticesProvider>;
